import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import beTimeOffCategories from 'actions/timeoff/beTimeOffCategories.action';
import beTimeOffReport from 'actions/timeoff/beTimeOffReport.action';
import feUpdateTimeOffData from 'actions/timeoff/feUpdateTimeOffData.action';
import fromDateToDayOfTheWeek from 'bl/scheduler/fromDateToDayOfTheWeek.bl';
import calculateFilteredRangeOfHours from 'bl/timeoff/calculateFilteredRangeOfHours.bl';
import determineDefaultTimeOffCategory from 'bl/timeoff/determineDefaultTimeOffCategory.bl';
import Note from 'components/form/Note.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Icon from 'components/generic/Icon.component';
import AllowanceTable from 'components/sidebar/timeOff/AllowanceTable.component';
import CategoryTimeOff from 'components/sidebar/timeOff/CategoryTimeOff.component';
import WidgetBlockDuration from 'components/sidebar/timeOff/WidgetBlockDuration.component';
import WidgetDayDuration from 'components/sidebar/timeOff/WidgetDayDuration.component';
import WidgetDuration from 'components/sidebar/timeOff/WidgetDuration.component';
import WidgetLongerDuration from 'components/sidebar/timeOff/WidgetLongerDuration.component';
import WidgetRepeatPeriod from 'components/sidebar/timeOff/WidgetRepeatPeriod.component';
import WidgetRepeatUntil from 'components/sidebar/timeOff/WidgetRepeatUntil.component';
import Workdays from 'components/sidebar/timeOff/Workdays.component';
import FLAG from 'config/flag.singleton';
import RRS from 'constants/recurrence/recurrenceRuleSimplified.const';
import TIME_OFF_DURATIONS from 'constants/timeoff/durations.const';
import TIME_OFF_REPEAT from 'constants/timeoff/repeat.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectBookingAllDay, selectBookingCurrentStylistId } from 'selectors/booking.selectors';
import { selectCurrentSalonId, selectCurrentSalonOpeningHours } from 'selectors/currentSalon.selectors';
import { selectTimeOffCategories, selectTimeOffForm, selectTimeOffReportDto } from 'selectors/timeoff.selectors';
import getAnyOrNull from 'utils/accessors/getAnyOrNull.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import parseJson from 'utils/parsers/parseJson';

import { setAllDay } from 'booking/actions';

import CN from './TimeOffPanel.module.scss';

const L = logger('TimeOffPanel');

const USE_WORKDAYS = false;

const REPEATS = Object.freeze(Object.values(TIME_OFF_REPEAT));

const TimeOffPanel = ({
  allDay: allDayFromProps,
  beTimeOffCategories,
  beTimeOffReport,
  currentStylistId,
  categories,
  feUpdateTimeOffData,
  form,
  report,
  openingHours,
  salonId,
  setAllDay,
  tag: tagFromProps,
}) => {
  const { t } = useTranslation();
  const tag = fromAnyToTrimmed(tagFromProps);

  const { duration, repeat, block, category, repeatUntil, referenceDate, fullDay } = asObject(form);

  const isDisabled = !category;
  const categoryId = asObject(category).id;
  const isRepeating = repeat !== TIME_OFF_REPEAT[RRS.onlyOnce];
  const allowancesMap = getAnyOrNull(fromAnyToString(currentStylistId), report);
  const selectedAllowance = getAnyOrNull(fromAnyToString(categoryId), allowancesMap);
  const { allowance } = asObject(selectedAllowance);
  const dayOfWeek = fromDateToDayOfTheWeek(asObject(block).day);

  const formattedRangeOfHours = useMemo(
    //
    () => calculateFilteredRangeOfHours({ hours: openingHours, dayOfWeek, expanded: FLAG.expandDayOffHours }),
    [dayOfWeek, openingHours],
  );

  const { val: isAlertRendered, yay: renderAlert } = useTip(false);

  const defaultCategory = determineDefaultTimeOffCategory({ categories });
  const showAlert = useCallback(
    (category) => {
      if (!category && category !== defaultCategory) return;
      renderAlert();
    },
    [defaultCategory, renderAlert],
  );

  const handleChange = useCallback(({ name, raw }) => void feUpdateTimeOffData({ [name]: raw }), [feUpdateTimeOffData]);

  const handleDuration = useCallback(
    ({ name, raw }) => {
      L.debug('handleDuration()', { name, raw });

      const isDaySelected = raw === TIME_OFF_DURATIONS.day;
      const isOnlySelected = isDaySelected && !allDayFromProps;
      const isOnlyFromProps = !isDaySelected && allDayFromProps;

      if (isOnlySelected || isOnlyFromProps) setAllDay();

      feUpdateTimeOffData({ [name]: raw });
    },
    [allDayFromProps, feUpdateTimeOffData, setAllDay],
  );

  useEffect(() => {
    L.unignore('useEffect()', 'beTimeOffCategories', beTimeOffCategories({ salonId }));
    L.unignore('useEffect()', 'beTimeOffReport:', beTimeOffReport({ salonId }));
  }, [beTimeOffCategories, beTimeOffReport, salonId]);

  // just logging the field values on change, instead of every render
  const fieldsJson = JSON.stringify(form);
  useEffect(() => void L.debug('()', 'form:', parseJson(fieldsJson)), [fieldsJson]);

  return (
    <div data-bem="TimeOffPanel" data-tag={tag} className={CN.component}>
      <CategoryTimeOff tag="TimeOffPanel__category" name="category" options={categories} onChange={handleChange} selected={category} />

      {fromAnyToBool(category && allowance) && <AllowanceTable tag="TimeOffPanel__allowance" value={selectedAllowance} />}

      {isAlertRendered && isDisabled && (
        <div className={CN.alertContainer}>
          <AlertPanel tag="CategoryTimeOff__alert" variant="warning">
            <Icon variant="warning" />
            {t('scheduler.timeOff.selectCategory')}
          </AlertPanel>
        </div>
      )}
      <div
        className={cn({
          [CN.hideComponents]: isDisabled,
        })}
        role="button"
        tabIndex={0}
        onClick={showAlert}
      >
        <WidgetDuration tag="TimeOffPanel__duration" name="duration" disabled={isDisabled} selected={duration} onChange={handleDuration} />
        {duration === TIME_OFF_DURATIONS.block && (
          <WidgetBlockDuration name="block" tag="TimeOffPanel__block" selected={block} disabled={isDisabled} options={formattedRangeOfHours} onChange={handleChange} />
        )}
        {duration === TIME_OFF_DURATIONS.day && <WidgetDayDuration tag="TimeOffPanel__day" disabled={isDisabled} name="fullDay" onChange={handleChange} value={fullDay} />}
        {duration !== TIME_OFF_DURATIONS.longer && <WidgetRepeatPeriod tag="TimeOffPanel__repeat" name="repeat" selected={repeat} disabled={isDisabled} options={REPEATS} onChange={handleChange} />}
        {duration === TIME_OFF_DURATIONS.longer && <WidgetLongerDuration name="longer" tag="TimeOffPanel__longer" onChange={handleChange} />}
        {USE_WORKDAYS && isRepeating && <Workdays tag="TimeOffPanel__workdays" name="workdays" onChange={handleChange} />}
        {isRepeating && <WidgetRepeatUntil tag="TimeOffPanel__repeat-until" name="repeatUntil" onChange={handleChange} referenceDate={referenceDate} selected={repeatUntil} />}
        <div className={CN.noteContainer}>
          <Note disabled={isDisabled} tag="TimeOffPanel__note" name="note" autoscroll onChange={handleChange} />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  categories: selectTimeOffCategories(state),
  currentStylistId: selectBookingCurrentStylistId(state),
  form: selectTimeOffForm(state),
  report: selectTimeOffReportDto(state),
  salonId: selectCurrentSalonId(state),
  openingHours: selectCurrentSalonOpeningHours(state),
  allDay: selectBookingAllDay(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ beTimeOffCategories, beTimeOffReport, feUpdateTimeOffData, setAllDay }, dispatch);

const TimeOffPanelHoc = connect(mapStateToProps, mapDispatchToProps)(TimeOffPanel);

// noinspection JSUnusedGlobalSymbols
export default TimeOffPanelHoc;
